/**
 * Minimalistický, bezpečný markdown renderer (bez závislostí, bez dangerouslySetInnerHTML).
 * Podporuje: # / ## / ### nadpisy, odstavce, odrážkové seznamy (- / *),
 * **tučné**, *kurzíva*, `kód` a [odkaz](url). Vše se skládá do React uzlů,
 * takže vstup nemůže vložit HTML (XSS-safe). Pokrývá formát `description_md` v seedu.
 */

import { Fragment, type ReactNode } from 'react';

/**
 * Inline formátování v jednom řádku textu → pole React uzlů.
 *
 * S `highlightPlaceholders` se navíc zvýrazní pole `[DOPLNÍ PRODEJCE: …]`
 * (žlutě), aby je majitel v právních textech snadno našel. Bez tohoto přepínače
 * se placeholder vykreslí jako běžný text (chování pro popisy produktů).
 */
function renderInline(text: string, highlightPlaceholders = false): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Pořadí: placeholder prodejce, odkaz, tučné, kurzíva, kód.
  const pattern =
    /(\[DOPLNÍ PRODEJCE:[^\]]*\])|\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      // Doplňovací pole prodejce.
      nodes.push(
        highlightPlaceholders ? (
          <mark
            key={key++}
            className="rounded bg-accent-soft px-1 font-semibold text-ink ring-1 ring-accent/60"
          >
            {m[1]}
          </mark>
        ) : (
          m[1]
        ),
      );
    } else if (m[2] !== undefined && m[3] !== undefined) {
      const href = m[3];
      const safe = /^(https?:|mailto:|\/)/i.test(href) ? href : '#';
      nodes.push(
        <a
          key={key++}
          href={safe}
          className="font-medium text-board underline decoration-accent underline-offset-2 hover:text-board-deep"
          {...(safe.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {m[2]}
        </a>,
      );
    } else if (m[4] !== undefined) {
      nodes.push(<strong key={key++}>{m[4]}</strong>);
    } else if (m[5] !== undefined) {
      nodes.push(<em key={key++}>{m[5]}</em>);
    } else if (m[6] !== undefined) {
      nodes.push(
        <code key={key++} className="rounded bg-paper2 px-1 py-0.5 font-mono text-[0.9em]">
          {m[6]}
        </code>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

type Block =
  | { type: 'h'; level: 2 | 3 | 4; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'p'; text: string }
  | { type: 'hr' }
  | { type: 'quote'; lines: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] };

/** Rozdělí řádek tabulky „| a | b |" na buňky (ořízne okrajové oddělovače). */
function tableCells(line: string): string[] {
  return line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** Je řádek oddělovač hlavičky tabulky (např. „|---|:--:|")? */
function isTableSeparator(line: string): boolean {
  return /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');
}

function parse(md: string): Block[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: string[] = [];
  let ordered: string[] = [];
  let quote: string[] = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: 'p', text: para.join(' ') });
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push({ type: 'ul', items: list });
      list = [];
    }
  };
  const flushOrdered = () => {
    if (ordered.length) {
      blocks.push({ type: 'ol', items: ordered });
      ordered = [];
    }
  };
  const flushQuote = () => {
    if (quote.length) {
      blocks.push({ type: 'quote', lines: quote });
      quote = [];
    }
  };
  const flushAll = () => {
    flushPara();
    flushList();
    flushOrdered();
    flushQuote();
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = (lines[i] ?? '').trimEnd();
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const numbered = /^\d+\.\s+(.*)$/.exec(line);
    const quoted = /^>\s?(.*)$/.exec(line);
    const isHr = /^(-{3,}|\*{3,}|_{3,})$/.test(line.trim());

    if (line.trim() === '') {
      flushAll();
    } else if (isHr) {
      flushAll();
      blocks.push({ type: 'hr' });
    } else if (line.trimStart().startsWith('|') && lines[i + 1] && isTableSeparator(lines[i + 1] ?? '')) {
      // Tabulka: hlavička + oddělovač + řádky (dokud řádek začíná „|").
      flushAll();
      const headers = tableCells(line);
      i += 1; // přeskoč oddělovač
      const rows: string[][] = [];
      while (i + 1 < lines.length && (lines[i + 1] ?? '').trimStart().startsWith('|')) {
        i += 1;
        rows.push(tableCells(lines[i] ?? ''));
      }
      blocks.push({ type: 'table', headers, rows });
    } else if (heading) {
      flushAll();
      // # a ## mapujeme na h2/h3 (h1 patří názvu stránky/produktu).
      const hashes = heading[1] ?? '#';
      const level = Math.min(4, Math.max(2, hashes.length + 1)) as 2 | 3 | 4;
      blocks.push({ type: 'h', level, text: heading[2] ?? '' });
    } else if (quoted) {
      flushPara();
      flushList();
      flushOrdered();
      quote.push(quoted[1] ?? '');
    } else if (bullet) {
      flushPara();
      flushOrdered();
      flushQuote();
      list.push(bullet[1] ?? '');
    } else if (numbered) {
      flushPara();
      flushList();
      flushQuote();
      ordered.push(numbered[1] ?? '');
    } else {
      flushList();
      flushOrdered();
      flushQuote();
      para.push(line.trim());
    }
  }
  flushAll();
  return blocks;
}

export default function Markdown({
  source,
  className,
  highlightPlaceholders = false,
}: {
  source: string;
  className?: string;
  /** Zvýraznit pole `[DOPLNÍ PRODEJCE: …]` (žlutě), použité v právních konceptech. */
  highlightPlaceholders?: boolean;
}) {
  const blocks = parse(source);
  const inline = (text: string) => renderInline(text, highlightPlaceholders);
  return (
    <div className={className}>
      {blocks.map((block, i) => {
        if (block.type === 'h') {
          const Tag = `h${block.level}` as 'h2' | 'h3' | 'h4';
          return (
            <Tag key={i} className="mb-2 mt-6 font-serif text-xl first:mt-0">
              {inline(block.text)}
            </Tag>
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={i} className="my-3 space-y-2">
              {block.items.map((item, j) => (
                <li key={j} className="flex gap-2.5">
                  <span
                    aria-hidden="true"
                    className="mt-2.5 h-1.5 w-1.5 flex-none rounded-full bg-accent"
                  />
                  <span>{inline(item)}</span>
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === 'ol') {
          return (
            <ol key={i} className="my-3 list-decimal space-y-2 pl-6 marker:text-ink-soft">
              {block.items.map((item, j) => (
                <li key={j} className="pl-1">
                  {inline(item)}
                </li>
              ))}
            </ol>
          );
        }
        if (block.type === 'hr') {
          return <hr key={i} className="my-6 border-line" />;
        }
        if (block.type === 'quote') {
          return (
            <blockquote
              key={i}
              className="my-4 rounded-r-card border-l-4 border-accent bg-paper2 px-4 py-3 text-sm text-ink-soft"
            >
              {block.lines.map((ln, j) => (
                <p key={j} className={j > 0 ? 'mt-2' : undefined}>
                  {inline(ln)}
                </p>
              ))}
            </blockquote>
          );
        }
        if (block.type === 'table') {
          return (
            <div key={i} className="my-4 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    {block.headers.map((h, j) => (
                      <th
                        key={j}
                        className="border border-line bg-paper2 px-3 py-2 text-left font-semibold text-ink"
                      >
                        {inline(h)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, c) => (
                        <td key={c} className="border border-line px-3 py-2 align-top text-ink-soft">
                          {inline(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return (
          <p key={i} className="my-3 leading-relaxed text-ink-soft first:mt-0">
            <Fragment>{inline(block.text)}</Fragment>
          </p>
        );
      })}
    </div>
  );
}
